import {
  VariationsProductDetailsRecord,
  VariationsDetailGridMetricType
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { GetEntityMetadataResponseRecord } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import { safeDivide } from 'src/utils/app';

type CustomizedTableValue = { value: number; prevValue: number | null };

interface StandardTableRecord {
  parentSku: string;
  stacklineSku: string;
  expectedParent?: string;
  productMetadata?: GetEntityMetadataResponseRecord;
  retailSales?: CustomizedTableValue;
  childSkuCount?: CustomizedTableValue;
  salesPerSku?: CustomizedTableValue;
  inventory?: CustomizedTableValue;
}

type PageDetailGridRecord = VariationsProductDetailsRecord & {
  metricType: VariationsDetailGridMetricType.PageDetailGrid;
};

type ChildDetailGridRecord = VariationsProductDetailsRecord & {
  metricType: VariationsDetailGridMetricType.ChildDetailGrid;
};

const isPageDetailGridRecord = (record: VariationsProductDetailsRecord): record is PageDetailGridRecord => {
  return record.metricType === VariationsDetailGridMetricType.PageDetailGrid;
};

const safeParseFloat = (value: string | undefined | null, defaultValue: number = 0): number => {
  try {
    if (value === undefined || value === null || value === '') {
      return defaultValue;
    }
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  } catch (error) {
    console.warn('Error parsing float value:', value, error);
    return defaultValue;
  }
};

export const filterRecordsByMetricType = (
  records: VariationsProductDetailsRecord[],
  metricType: VariationsDetailGridMetricType
): VariationsProductDetailsRecord[] => {
  try {
    if (!Array.isArray(records)) {
      console.warn('filterRecordsByMetricType: records is not an array, returning empty array');
      return [];
    }
    return records.filter((record) => {
      try {
        return record && record.metricType === metricType;
      } catch (error) {
        console.warn('Error filtering record:', record, error);
        return false;
      }
    });
  } catch (error) {
    console.error('Error in filterRecordsByMetricType:', error);
    return [];
  }
};

export const aggregatePageDetailGridRecord = (
  currentPeriodRecord: PageDetailGridRecord,
  prevPeriodRecord: PageDetailGridRecord
): void => {
  try {
    const prevRetailSales = safeParseFloat(prevPeriodRecord.retailSales);
    const currentRetailSales = safeParseFloat(currentPeriodRecord.retailSales);

    prevPeriodRecord.retailSales = (prevRetailSales + currentRetailSales).toString();

    const prevChildCount = prevPeriodRecord.childSkuCount;
    const currentChildCount = currentPeriodRecord.childSkuCount;
    prevPeriodRecord.childSkuCount = prevChildCount + currentChildCount;

    const totalSales = safeParseFloat(prevPeriodRecord.retailSales);
    const totalChildren = prevPeriodRecord.childSkuCount;
    const salesPerSku = safeDivide(totalSales, totalChildren);
    prevPeriodRecord.salesPerSku = salesPerSku.toString();
  } catch (error) {
    console.error('Error in aggregatePageDetailGridRecord:', error);
  }
};

export const buildPeriodMap = (
  currentPeriodRecords: VariationsProductDetailsRecord[],
  metricType: VariationsDetailGridMetricType
): Map<string, VariationsProductDetailsRecord> => {
  try {
    const filteredCurrentPeriodRecords = filterRecordsByMetricType(currentPeriodRecords, metricType);
    const periodMap = new Map<string, VariationsProductDetailsRecord>();

    filteredCurrentPeriodRecords.forEach((currentRecord) => {
      try {
        if (!currentRecord || !currentRecord.stacklineSku) {
          console.warn('buildPeriodMap: Skipping invalid record:', currentRecord);
          return;
        }

        const key = currentRecord.stacklineSku;
        const existingRecord = periodMap.get(key);

        if (!existingRecord) {
          periodMap.set(key, { ...currentRecord });
        } else {
          if (
            metricType === VariationsDetailGridMetricType.PageDetailGrid &&
            isPageDetailGridRecord(currentRecord) &&
            isPageDetailGridRecord(existingRecord)
          ) {
            aggregatePageDetailGridRecord(currentRecord, existingRecord);
          }
        }
      } catch (error) {
        console.warn('Error processing record in buildPeriodMap:', currentRecord, error);
      }
    });

    return periodMap;
  } catch (error) {
    console.error('Error in buildPeriodMap:', error);
    return new Map<string, VariationsProductDetailsRecord>();
  }
};

export const addRetailSalesData = (
  result: StandardTableRecord,
  currentPeriodRecord: VariationsProductDetailsRecord,
  prevPeriodRecord?: VariationsProductDetailsRecord
): void => {
  try {
    const currentRetailSales = safeParseFloat(currentPeriodRecord.retailSales);
    const prevRetailSales = prevPeriodRecord ? safeParseFloat(prevPeriodRecord.retailSales) : null;

    result.retailSales = {
      value: currentRetailSales,
      prevValue: prevRetailSales
    };
  } catch (error) {
    console.error('Error in addRetailSalesData:', error);
    result.retailSales = { value: 0, prevValue: null };
  }
};

export const addPageDetailGridData = (
  result: StandardTableRecord,
  currentPeriodRecord: PageDetailGridRecord,
  prevPeriodRecord?: VariationsProductDetailsRecord
): void => {
  try {
    const currentChildCount = currentPeriodRecord.childSkuCount;
    const currentSalesPerSku = safeParseFloat(currentPeriodRecord.salesPerSku);
    const prevChildCount =
      prevPeriodRecord && isPageDetailGridRecord(prevPeriodRecord) ? prevPeriodRecord.childSkuCount : null;
    const prevSalesPerSku =
      prevPeriodRecord && isPageDetailGridRecord(prevPeriodRecord)
        ? safeParseFloat(prevPeriodRecord.salesPerSku)
        : null;

    result.childSkuCount = { value: currentChildCount, prevValue: prevChildCount };
    result.salesPerSku = { value: currentSalesPerSku, prevValue: prevSalesPerSku };
  } catch (error) {
    console.error('Error in addPageDetailGridData:', error);
    result.childSkuCount = { value: 0, prevValue: null };
    result.salesPerSku = { value: 0, prevValue: null };
  }
};

export const addChildDetailGridData = (
  result: StandardTableRecord,
  currentPeriodRecord: ChildDetailGridRecord,
  prevPeriodRecord?: VariationsProductDetailsRecord
): void => {
  try {
    result.expectedParent = currentPeriodRecord.expectedParent || '';
    const currentInventory = safeParseFloat(currentPeriodRecord.inventory);
    const prevInventory =
      prevPeriodRecord && !isPageDetailGridRecord(prevPeriodRecord)
        ? safeParseFloat((prevPeriodRecord as ChildDetailGridRecord).inventory)
        : null;

    result.inventory = { value: currentInventory, prevValue: prevInventory };
  } catch (error) {
    console.error('Error in addChildDetailGridData:', error);
    result.expectedParent = '';
    result.inventory = { value: 0, prevValue: null };
  }
};

export const transformRecord = (
  currentPeriodRecord: VariationsProductDetailsRecord,
  prevPeriodMap: Map<string, VariationsProductDetailsRecord>,
  productMetadataMap: Map<string, GetEntityMetadataResponseRecord>
): StandardTableRecord => {
  try {
    if (!currentPeriodRecord || !currentPeriodRecord.stacklineSku) {
      console.warn('transformRecord: Invalid currentPeriodRecord:', currentPeriodRecord);
      return {
        parentSku: '',
        stacklineSku: '',
        productMetadata: undefined
      };
    }

    const prevPeriodRecord = prevPeriodMap?.get(currentPeriodRecord.stacklineSku);
    const productMetadata = productMetadataMap?.get(currentPeriodRecord.stacklineSku);

    const result: StandardTableRecord = {
      parentSku: currentPeriodRecord.parentSku || '',
      stacklineSku: currentPeriodRecord.stacklineSku,
      productMetadata: productMetadata
    };

    addRetailSalesData(result, currentPeriodRecord, prevPeriodRecord);

    const isCurrentPageDetailGrid = isPageDetailGridRecord(currentPeriodRecord);

    if (isCurrentPageDetailGrid) {
      addPageDetailGridData(result, currentPeriodRecord, prevPeriodRecord);
    } else {
      addChildDetailGridData(result, currentPeriodRecord as ChildDetailGridRecord, prevPeriodRecord);
    }

    return result;
  } catch (error) {
    console.error('Error in transformRecord:', error);
    return {
      parentSku: currentPeriodRecord?.parentSku || '',
      stacklineSku: currentPeriodRecord?.stacklineSku || '',
      productMetadata: undefined,
      retailSales: { value: 0, prevValue: null }
    };
  }
};

export type { GetEntityMetadataResponseRecord, StandardTableRecord, PageDetailGridRecord, ChildDetailGridRecord };
